import * as React from "react";
import { AlertColor, MenuItem, Stack, TextField } from "@mui/material";
import CclButton from "../../../components/common/cclButtons/cclButton";
import CclLoadingButton from "../../../components/common/cclButtons/cclLoadingButton";
import CclDrawer from "../../../components/common/cclDrawer/cclDrawer";
import CclDrawerActionBar from "../../../components/common/cclDrawer/cclDrawerActionBar";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";
import {
    useAddAdminLocationMutation,
    useEditAdminLocationMutation,
} from "../../../services/cclTokenedPayrollApi";
import { UserClaimsService } from "../../../services/currentUserService/currentUserService";
import { PayrollAdminLocation } from "../../../services/types/payrollEventTypes";

interface AddEditAdminLocationDrawerProps {
    open: boolean;
    mode: "add" | "edit";
    location: PayrollAdminLocation | null;
    onClose: () => void;
    onSaveResult: (message: string, severity: AlertColor) => void;
}

const AddEditAdminLocationDrawer: React.FC<AddEditAdminLocationDrawerProps> = (props) => {
    const isEditMode = props.mode === "edit";
    const [locCode, setLocCode] = React.useState("");
    const [locDesc, setLocDesc] = React.useState("");
    const [active, setActive] = React.useState(true);
    const [addAdminLocation, { isLoading: isAdding }] = useAddAdminLocationMutation();
    const [editAdminLocation, { isLoading: isEditing }] = useEditAdminLocationMutation();
    const { logEvent } = useLogAccessEvent();
    const claimservice = new UserClaimsService();

    React.useEffect(() => {
        if (!props.open) {
            return;
        }

        setLocCode(props.location?.locCode ?? "");
        setLocDesc(props.location?.locDesc ?? "");
        setActive(props.location?.active ?? true);
    }, [props.open, props.location]);

    const isSaving = isAdding || isEditing;
    const canSave = locCode.trim() !== "" && locDesc.trim() !== "" && !isSaving;

    const handleSave = () => {
        const locationValues = {
            locCode: locCode.trim(),
            locDesc: locDesc.trim(),
            active: true,
        };
        const saveAction = isEditMode && props.location
            ? editAdminLocation({
                ...props.location,
                ...locationValues,
            })
            : addAdminLocation({
                ...locationValues,
            });

        saveAction
            .unwrap()
            .then(async () => {
                await logEvent(
                    isEditMode
                        ? "PayrollAdminLocationEdited"
                        : "PayrollAdminLocationAdded",
                    null
                );
                props.onSaveResult(
                    isEditMode ? "Admin. Location Edited" : "Admin. Location Added",
                    "success"
                );
                props.onClose();
            })
            .catch((error) => {
                console.error("Error saving admin location:", error);
                props.onSaveResult(
                    isEditMode
                        ? "Failed to edit Admin Location"
                        : "Failed to add Admin Location",
                    "error"
                );
            });
    };

    return (
        <CclDrawer
            title={isEditMode ? "Edit Admin Location" : "Add Admin Location"}
            open={props.open}
            onClose={props.onClose}
        >
            <Stack spacing={2} sx={{ pt: 1 }}>
                <TextField
                    id="add-edit-admin-location-code"
                    label="Code"
                    size="small"
                    fullWidth
                    disabled={isEditMode}
                    value={locCode}
                    onChange={(event) => setLocCode(event.target.value)}
                />
                <TextField
                    id="add-edit-admin-location-description"
                    label="Description"
                    size="small"
                    fullWidth
                    value={locDesc}
                    onChange={(event) => setLocDesc(event.target.value)}
                />
                <CclDrawerActionBar>
                    <CclLoadingButton
                        mode="primary"
                        loading={isSaving}
                        disabled={!canSave}
                        onClick={handleSave}
                    >
                        Save
                    </CclLoadingButton>
                    <CclButton mode="secondary" onClick={props.onClose}>
                        Cancel
                    </CclButton>
                </CclDrawerActionBar>
            </Stack>
        </CclDrawer>
    );
};

export default AddEditAdminLocationDrawer;